import { Link } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import {
  ListInlineRenameActions,
  ListInlineRenameField,
} from "@/modules/shell/ListInlineRename";
import type { GlAccount } from "@/shared/api/accounting";

const columnHelper = createColumnHelper<DeskTableFeatures, GlAccount>();

export type ChartOfAccountsColumnsOpts = {
  companySlug: string;
  busy: boolean;
  editingId: string | null;
  editName: string;
  setEditName: (v: string) => void;
  onStartEdit: (row: GlAccount) => void;
  onCancelEdit: () => void;
  onSaveRename: () => void;
  onDeactivate: (row: GlAccount) => void;
  onReactivate: (row: GlAccount) => void;
};

export function chartOfAccountsColumns(opts: ChartOfAccountsColumnsOpts) {
  return columnHelper.columns([
    columnHelper.accessor("code", {
      id: "code",
      header: "Code",
      size: 96,
      cell: (info) => (
        <span className="font-mono text-sm">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor("name", {
      id: "name",
      header: "Name",
      size: 240,
      cell: ({ row }) => {
        const a = row.original;
        return (
          <ListInlineRenameField
            name={a.name}
            editing={opts.editingId === a.id}
            editValue={opts.editName}
            onEditValueChange={opts.setEditName}
            onSave={opts.onSaveRename}
            onCancel={opts.onCancelEdit}
            saving={opts.busy}
          />
        );
      },
    }),
    columnHelper.accessor("type", {
      id: "type",
      header: "Type",
      size: 120,
      cell: ({ row }) => {
        const a = row.original;
        return (
          <span className="text-xs text-[var(--color-muted)]">
            {a.type}
            {a.isSystem ? " · system" : ""}
            {a.deactivatedAt ? " · hidden" : ""}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 220,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const a = row.original;
        const editing = opts.editingId === a.id;
        if (editing) {
          return (
            <ListInlineRenameActions
              editing
              editValue={opts.editName}
              saving={opts.busy}
              onStartEdit={() => opts.onStartEdit(a)}
              onSave={opts.onSaveRename}
              onCancel={opts.onCancelEdit}
            />
          );
        }
        return (
          <div className="flex flex-wrap justify-end gap-1">
            <ListInlineRenameActions
              editing={false}
              editValue={opts.editName}
              startDisabled={opts.busy}
              onStartEdit={() => opts.onStartEdit(a)}
              onSave={opts.onSaveRename}
              onCancel={opts.onCancelEdit}
            />
            <Button variant="ghost" size="sm" asChild>
              <Link
                to={`/c/${opts.companySlug}/gl/accounts/${a.id}`}
                className="no-underline"
              >
                Ledger
              </Link>
            </Button>
            {a.deactivatedAt ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={opts.busy}
                onClick={() => opts.onReactivate(a)}
              >
                Show
              </Button>
            ) : !a.isSystem ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={opts.busy}
                onClick={() => opts.onDeactivate(a)}
              >
                Hide
              </Button>
            ) : null}
          </div>
        );
      },
    }),
  ]);
}
